import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationDetailsDto,
    LocationDto,
    LocationService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getFeatureGroups } from '../helpers';

interface TemplateFeatureRow {
    /** The index of the feature in the template's features. */
    index: number;
    name: string;
    /** Shown as a subtitle, null if the category could not be determined. */
    categoryName: string | null;
}

interface TemplateFeatureSection {
    name: string;
    rows: TemplateFeatureRow[];
}

/**
 * Lets the user choose which features of a location template to use, with a
 * map preview of the selection. It also acts as the confirmation when the
 * template will replace an existing location.
 */
@Component({
    selector: 'location-template-features-dialogue',
    templateUrl: './location-template-features-dialogue.component.html',
    styleUrls: ['./location-template-features-dialogue.component.scss']
})
export class LocationTemplateFeaturesDialogue {
    @Input() organisationId: number;
    /** The templates that can be selected within the dialogue. */
    @Input() templates: LocationDto[];
    /** The currently loaded template, including its features. */
    @Input() template: LocationDetailsDto;
    /** Whether an existing mission location will be replaced. */
    @Input() replacesExisting: boolean;

    /** Emits the template with only the selected features. */
    @Output() useFeatures = new EventEmitter<LocationDetailsDto>();

    sections: TemplateFeatureSection[] = [];
    selectedTemplateId: number;

    /** Indexes into template.features. */
    selectedIndexes = new Set<number>();
    selectedCount = 0;
    previewFeatures: FeatureGroup[] = [];

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    working: boolean;

    constructor(
        private modal: BsModalRef,
        private locationService: LocationService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.selectedTemplateId = this.template.id;
        this.loadTemplate(this.template);
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onTemplateChanged(selected: LocationDto) {
        this.locationService
            .findLocation(selected.id, selected.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: template => this.loadTemplate(template),
                error: (error: FlyFreelyError) => {
                    this.selectedTemplateId = this.template.id;
                    this.logging.error(
                        error,
                        `Error loading location template: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    toggleFeature(index: number) {
        const next = new Set(this.selectedIndexes);
        if (next.has(index)) {
            next.delete(index);
        } else {
            next.add(index);
        }
        this.setSelection(next);
    }

    toggleSection(section: TemplateFeatureSection) {
        const next = new Set(this.selectedIndexes);
        const allSelected = this.isSectionSelected(section);
        section.rows.forEach(row => {
            if (allSelected) {
                next.delete(row.index);
            } else {
                next.add(row.index);
            }
        });
        this.setSelection(next);
    }

    isSectionSelected(section: TemplateFeatureSection) {
        return section.rows.every(row => this.selectedIndexes.has(row.index));
    }

    countSelected(section: TemplateFeatureSection) {
        return section.rows.filter(row => this.selectedIndexes.has(row.index))
            .length;
    }

    selectAll() {
        this.setSelection(new Set(this.template.features.map((_, ix) => ix)));
    }

    selectNone() {
        this.setSelection(new Set());
    }

    apply() {
        this.useFeatures.emit({
            ...this.template,
            features: this.template.features.filter((_, ix) =>
                this.selectedIndexes.has(ix)
            )
        });
        this.modal.hide();
    }

    cancel() {
        this.modal.hide();
    }

    hide() {
        this.modal.hide();
    }

    private loadTemplate(template: LocationDetailsDto) {
        this.template = template;
        this.buildSections(template);
        this.selectAll();
    }

    private buildSections(template: LocationDetailsDto) {
        const { features: groups } = getFeatureGroups(template.features, 0);
        this.sections = groups
            .filter(group => group.existingFeatures.length > 0)
            .map(group => ({
                name: group.name,
                rows: group.existingFeatures.map(feature => ({
                    // The feature id is its index in template.features
                    index: feature.id as number,
                    name: feature.name,
                    categoryName:
                        group.categories?.find(c => c.id === feature.categoryId)
                            ?.name ?? null
                }))
            }));
    }

    /**
     * All selection changes go through here so that the preview features are
     * only recreated when the selection changes. The map zooms to the features
     * every time it receives a new array.
     */
    private setSelection(selection: Set<number>) {
        this.selectedIndexes = selection;
        const selected = this.template.features.filter((_, ix) =>
            selection.has(ix)
        );
        this.selectedCount = selected.length;
        this.previewFeatures = getFeatureGroups(selected, 0).features;
    }
}
